import React from 'react'
import { Form } from 'react-final-form'
import AsyncAutoCompleteFormItem from '../../../shared/components/asyncAutoCompleteFormItem'
import { Button, Grid, Checkbox, TextField, InputAdornment } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { filterStyles } from './FilterStyles'
import { useSelector, useDispatch } from 'react-redux'
import {
    searchSessions,
    filterByUser,
    filterByInitSource,
    filterWithInvoiceOnly,
    filterByConnector,
    filterByPaymentStatus
} from '../../../redux/actions/sessions'
import { UserClassProfile } from '../../../shared/types/user'
import { ChargerConnectorType } from '../../../shared/types/charger'
import Typography from '@material-ui/core/Typography'
import SearchIcon from '@material-ui/icons/Search'
import { ChargingInitSources, PaymentStatus } from '../../../shared/types/chargingSession'

export default function HistoryFilter() {
    const searchString = useSelector((state: any) => state.sessions.filter.search.value)

    const connectorTypes = useSelector((state: any) => state.sessions.filter.connectorType.value)
    const userTypes = useSelector((state: any) => state.sessions.filter.userType.value)
    const chargingInitSourceTypes = useSelector((state: any) => state.sessions.filter.chargingInitSource.value)
    const paymentStatusTypes = useSelector((state: any) => state.sessions.filter.paymentStatus.value)
    const showOnlyWithInvoice = useSelector((state: any) => state.sessions.filter.showOnlyWithInvoice.value)

    const { t } = useTranslation()
    const classes = filterStyles()
    const dispatch = useDispatch()

    const handleSearchChanged = (newSearchString: string) => {
        dispatch(searchSessions(newSearchString))
    }

    const handleShowOnlyWithInvoice = () => {
        dispatch(filterWithInvoiceOnly(!showOnlyWithInvoice))
    }

    const onFormSubmit = (values: any) => {
        const filterConnectors: number[] =
            values.connectorTypes?.length
                ? [...values.connectorTypes].sort((a: number, b: number) => a - b)
                : []

        const filterUsers: number[] =
            values.userTypes?.length
                ? [...values.userTypes].sort((a: number, b: number) => a - b)
                : []

        const filterInitSources: number[] =
            values.chargingInitSourceTypes?.length
                ? [...values.chargingInitSourceTypes].sort((a: number, b: number) => a - b)
                : []

        const filterPaymentStatus: number[] =
            values.paymentStatusTypes?.length
                ? [...values.paymentStatusTypes].sort((a: number, b: number) => a - b)
                : []

        dispatch(filterByConnector(filterConnectors))
        dispatch(filterByUser(filterUsers))
        dispatch(filterByInitSource(filterInitSources))
        dispatch(filterByPaymentStatus(filterPaymentStatus))
    }

    const classProfileOptionMapper = (x: any) => x
    const typeConnectorOptionMapper = (x: any) => x
    const chargingInitSourceTypesMapper = (x: any) => x
    const paymentStatusTypesMapper = (x: any) => x

    const standardClassProfile = { id: UserClassProfile.Standard, label: t('users.standard') }
    const anonymousClassProfile = { id: UserClassProfile.Anonymous, label: t('users.anonymous') }
    const whitelistClassProfile = { id: UserClassProfile.Whitelist, label: t('users.whitelist') }

    const acType = { id: ChargerConnectorType.Ac, label: t('locations.connectorTypeAc') }
    const dcType = { id: ChargerConnectorType.Dc, label: t('locations.connectorTypeDc') }

    const SEAK = { id: ChargingInitSources.SEAK, label: 'SEAK' }
    const HUBJECT = { id: ChargingInitSources.HUBJECT, label: 'HUBJECT' }
    const VILLAPRO = { id: ChargingInitSources.VILLAPRO, label: 'VILLAPRO' }
    const PLUGNCHARGE = { id: ChargingInitSources.PLUGNCHARGE, label: 'PLUGNCHARGE' }
    const QVEND = { id: ChargingInitSources.QVEND, label: 'QVEND' }

    const FREE = { id: PaymentStatus.FREE, label: 'FREE' }
    const PAID = { id: PaymentStatus.PAID, label: 'PAID' }
    const UNPAID = { id: PaymentStatus.UNPAID, label: 'UNPAID' }
    const ROAMING = { id: PaymentStatus.ROAMING, label: 'ROAMING' }

    let userOptions: any[] = []
    if (userTypes) {
        userOptions = userTypes.map((type: number) => {
            switch (type) {
                case UserClassProfile.Standard:
                    return standardClassProfile
                case UserClassProfile.Anonymous:
                    return anonymousClassProfile
                case UserClassProfile.Whitelist:
                    return whitelistClassProfile
                default:
                    return null
            }
        })
    }

    let connectorOptions: any[] = []
    if (connectorTypes) {
        connectorOptions = connectorTypes.map((type: number) => {
            switch (type) {
                case ChargerConnectorType.Ac:
                    return acType
                case ChargerConnectorType.Dc:
                    return dcType
                default:
                    return null
            }
        })
    }

    let chargingInitSources: any[] = []
    if (chargingInitSourceTypes) {
        chargingInitSources = chargingInitSourceTypes.map((type: number) => {
            switch (type) {
                case ChargingInitSources.SEAK:
                    return SEAK
                case ChargingInitSources.HUBJECT:
                    return HUBJECT
                case ChargingInitSources.VILLAPRO:
                    return VILLAPRO
                case ChargingInitSources.PLUGNCHARGE:
                    return PLUGNCHARGE
                case ChargingInitSources.QVEND:
                    return QVEND
                default:
                    return null
            }
        })
    }

    let paymentStatusOptions: any[] = []
    if (paymentStatusTypes) {
        paymentStatusOptions = paymentStatusTypes.map((type: number) => {
            switch (type) {
                case PaymentStatus.FREE:
                    return FREE
                case PaymentStatus.PAID:
                    return PAID
                case PaymentStatus.UNPAID:
                    return UNPAID
                case PaymentStatus.ROAMING:
                    return ROAMING
                default:
                    return null
            }
        })
    }

    const classProfilesFetcher = () =>
        new Promise((resolve) => {
            resolve([standardClassProfile, anonymousClassProfile, whitelistClassProfile])
        })

    const connectorTypeFetcher = () =>
        new Promise((resolve) => {
            resolve([acType, dcType])
        })

    const chargingInitSourceFetcher = () =>
        new Promise((resolve) => {
            resolve([SEAK, HUBJECT, VILLAPRO, PLUGNCHARGE, QVEND])
        })

    const paymentStatusFetcher = () =>
        new Promise((resolve) => {
            resolve([FREE, PAID, UNPAID, ROAMING])
        })

    return (
        <Grid container className={classes.formWrapper}>
            <Grid item xs={12}>
                <Form
                    onSubmit={onFormSubmit}
                    render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit} noValidate>
                            <Grid container>
                                <Grid item xs={3} className={classes.searchWrapper}>
                                    <div>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            placeholder={t('history.search')}
                                            value={searchString}
                                            onChange={(e) => handleSearchChanged(e.target.value)}
                                            className={classes.input}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Typography align="left" variant="body1">
                                            {t('history.onlyWithInvoice')}
                                            <Checkbox
                                                name="showOnlyWithInvoice"
                                                style={{
                                                    color: '#6100ED',
                                                    padding: '4px',
                                                }}
                                                checked={showOnlyWithInvoice}
                                                onChange={handleShowOnlyWithInvoice}
                                            />
                                        </Typography>
                                    </div>
                                </Grid>

                                <Grid item xs={2} className={classes.filter}>
                                    <AsyncAutoCompleteFormItem
                                        form={form}
                                        label={t('users.userType')}
                                        field="userTypes"
                                        dataFetcher={classProfilesFetcher}
                                        optionMapper={classProfileOptionMapper}
                                        initialOptions={userOptions}
                                    />
                                </Grid>
                                <Grid item xs={2} className={classes.filter}>
                                    <AsyncAutoCompleteFormItem
                                        form={form}
                                        label={t('history.initSource')}
                                        field="chargingInitSourceTypes"
                                        dataFetcher={chargingInitSourceFetcher}
                                        optionMapper={chargingInitSourceTypesMapper}
                                        initialOptions={chargingInitSources}
                                    />
                                </Grid>
                                <Grid item xs={2} className={classes.filter}>
                                    <AsyncAutoCompleteFormItem
                                        form={form}
                                        label={t('history.connectorType')}
                                        field="connectorTypes"
                                        dataFetcher={connectorTypeFetcher}
                                        optionMapper={typeConnectorOptionMapper}
                                        initialOptions={connectorOptions}
                                    />
                                </Grid>
                                <Grid item xs={2} className={classes.filter}>
                                    <AsyncAutoCompleteFormItem
                                        form={form}
                                        label={t('history.paymentStatus')}
                                        field="paymentStatusTypes"
                                        dataFetcher={paymentStatusFetcher}
                                        optionMapper={paymentStatusTypesMapper}
                                        initialOptions={paymentStatusOptions}
                                    />
                                </Grid>
                                <Grid item xs={1} className={classes.filterButtonWrapper}>
                                    <Button variant="contained" color="primary" type="submit">
                                        {t('history.filter')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                />
            </Grid>
        </Grid>
    )
}
